import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { colors } from "utils";
import { getReportClassList } from "hooks/useFilterPersistence/useFilterPersistence";
import { resolveClassId } from "utils/persistFilters";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { Tab, TabList } from "components/shared/PageTabs/PageTabs";
import { HeadingBetaBadge } from "components/pages/Educator/StudentReports/BetaBadge";
import { StudentActivity } from "components/pages/Educator/StudentActivity/StudentActivity";
import { Proficiency } from "components/pages/Educator/Proficiency/Proficiency";
import { CollaborationTrend } from "components/pages/Educator/CollaborationTrend/CollaborationTrend";
import { useLandingPageView, useViewedPageTracking } from "hooks/usePageViewTracker";
import { pageTitleInset } from "components/pageTitleInset";

export const CLASS_REPORTS_PAGE_NAME = "Class Reports";

// One page, three reports. Each tab is the existing standalone report with its
// own header and Class dropdown hidden; the class is chosen once up here and
// handed down as `sharedClassId`. Keying the tab body on the class remounts
// the report on a class change, which is the only moment the reports read
// `sharedClassId` (their persistence hook resolves it on mount only).
export const TABS = [
  { key: "dosage", label: "Dosage", Component: StudentActivity },
  { key: "proficiency", label: "Proficiency", Component: Proficiency },
  { key: "collaboration", label: "Collaboration", Component: CollaborationTrend },
];

export const tabFromSearch = (search) => {
  const tab = new URLSearchParams(search).get("tab");
  return TABS.some((t) => t.key === tab) ? tab : TABS[0].key;
};

export const ClassReports = () => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const tab = tabFromSearch(location.search);
  const [classes, setClasses] = useState(null);
  const [classId, setClassId] = useState(null);
  const landingTab = useRef(TABS.find((t) => t.key === tab).label);
  const { trackView } = useViewedPageTracking();
  useLandingPageView(CLASS_REPORTS_PAGE_NAME, { tab: landingTab.current });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getReportClassList(queryClient, null);
        if (cancelled) return;
        setClasses(list);
        if (list.length > 0) {
          const resolved = await resolveClassId("class_reports", list);
          if (!cancelled) setClassId(resolved.classId);
        }
      } catch (e) {
        if (!cancelled) setClasses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  const selectTab = (key) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", key);
    history.push({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const active = TABS.find((t) => t.key === tab);
  const selectedClass = (classes || []).find((c) => c.id === classId);

  return (
    <Container>
      <PageHeader>
        <Title>Class Reports</Title>
        <HeadingBetaBadge>BETA</HeadingBetaBadge>
      </PageHeader>

      <FilterBand>
        <SelectDropdown
          label="Class"
          value={classId}
          displayText={classDisplayName(selectedClass) || "Select a class"}
          options={(classes || []).map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
          groupBySchoolYear
          onChange={setClassId}
        />
      </FilterBand>

      <TabList role="tablist" aria-label="Class report views">
        {TABS.map((entry) => (
          <Tab
            key={entry.key}
            role="tab"
            type="button"
            id={`tab-${entry.key}`}
            aria-controls={`panel-${entry.key}`}
            aria-selected={entry.key === tab}
            $active={entry.key === tab}
            onClick={() => {
              if (entry.key !== tab) trackView(CLASS_REPORTS_PAGE_NAME, { tab: entry.label });
              selectTab(entry.key);
            }}
          >
            {entry.label}
          </Tab>
        ))}
      </TabList>

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {classes && classes.length === 0 && (
          <Empty>Create a group to see class reports.</Empty>
        )}
        {classId && (
          <active.Component
            key={classId}
            embedded
            sharedClassId={classId}
            onClassChange={setClassId}
          />
        )}
      </div>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 92px);
  background-color: ${colors.milkGlass200};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${colors.nightlyWoods500};
  padding: 24px 32px 24px ${pageTitleInset};
`;

const Title = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

const FilterBand = styled.div`
  display: flex;
  padding: 24px 32px 16px;

  & > * {
    flex: 0 0 288px;
    min-width: 0;
  }
`;

const Empty = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  color: ${colors.nightlyWoods900};
  padding: 32px;
  margin: 0;
`;
